import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatCount, formatPct, prettyQuarter } from '@/lib/format';
import { useResolvedColors } from '@/lib/useResolvedColor';
import { ChartLegend } from './ChartLegend';
import { ChartTooltip } from './ChartTooltip';
import { QuarterRangeSlider } from './QuarterRangeSlider';
import { ChartSummary, Emph } from './ChartSummary';
import { QuarterTick, useQuarterWindow, profileChartStyles as s } from './ProfileSection';
import { formatSignedCount } from './QuarterlyPerformanceTable';
import { formatAxisCount } from './ProfileSection';

interface HistoryPoint {
  quarter: string;
  gross_additions: number;
  churns: number | null;
  net_additions: number | null;
  churn_rate: number | null;
}

export interface NetGrossChurnChartProps {
  platform: string;
  /** Scoped history, oldest → newest. */
  history: HistoryPoint[];
}

const LABEL_ALL_MAX = 12;

const SERIES = [
  { key: 'gross', label: 'Gross Additions', token: 'var(--metric-acquired)' },
  { key: 'net', label: 'Net Additions', token: 'var(--data-1)' },
  { key: 'churn', label: 'Churns', token: 'var(--metric-loss)' },
] as const;

/**
 * Net, Gross, Churn Over Time — the old platform page's signature chart: per
 * quarter, gross adds above zero, churns below it, and net adds either side.
 * Direction is carried by position and signed labels, not colour alone.
 */
export function NetGrossChurnChart({ platform, history }: NetGrossChurnChartProps) {
  // The oldest quarter has no churn / net figure — leave it out of this chart.
  const allPoints = useMemo(
    () =>
      history
        .filter((h) => h.churns != null && h.net_additions != null)
        .map((h) => ({
          quarter: h.quarter,
          label: prettyQuarter(h.quarter),
          gross: h.gross_additions,
          net: h.net_additions ?? 0,
          churn: -(h.churns ?? 0),
          churnRate: h.churn_rate,
        })),
    [history],
  );

  const [[lo, hi], setRange] = useQuarterWindow(allPoints.length);
  const points = allPoints.slice(lo, hi + 1);
  const colors = useResolvedColors(SERIES.map((x) => x.token));
  const showAllLabels = points.length <= LABEL_ALL_MAX;
  const last = points[points.length - 1];

  return (
    <>
      <ChartSummary className={s.summary}>
        {last ? (
          <>
            <Emph>{platform}</Emph> added <Emph>{formatCount(last.gross)}</Emph> and lost{' '}
            <Emph>{formatCount(-last.churn)}</Emph> subscribers in <Emph>{last.label}</Emph>, for net additions of{' '}
            <Emph>{formatSignedCount(last.net)}</Emph>
            {last.churnRate != null && (
              <>
                {' '}and churn of <Emph>{formatPct(last.churnRate)}</Emph>
              </>
            )}
            .
          </>
        ) : (
          '—'
        )}
      </ChartSummary>

      <div className={s.body}>
        {allPoints.length > 1 && (
          <QuarterRangeSlider
            count={allPoints.length}
            value={[lo, hi]}
            onChange={setRange}
            labels={allPoints.map((p) => p.label)}
          />
        )}

        {points.length ? (
          <>
            <div className={s.plot}>
              <span className={`${s.yAxisTitle} ${s.yAxisTitleLeft}`}>Subscribers</span>
              <ResponsiveContainer width="100%" height={440}>
                <BarChart data={points} barGap={3} barCategoryGap="18%" margin={{ top: 32, right: 8, left: 0, bottom: 4 }}>
                  <CartesianGrid stroke="var(--border-subtle)" vertical={false} />
                  <XAxis
                    dataKey="label"
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                    height={46}
                    tick={QuarterTick}
                  />
                  <YAxis
                    className={s.axis}
                    axisLine={false}
                    tickLine={false}
                    width={64}
                    tickFormatter={(v) => formatAxisCount(Number(v), true)}
                  />
                  <ReferenceLine y={0} stroke="var(--border-strong)" strokeWidth={1.5} />
                  <Tooltip
                    cursor={{ fill: 'var(--bg-hover)', opacity: 0.5 }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const p = payload[0].payload as (typeof points)[number];
                      return (
                        <ChartTooltip
                          title={label}
                          rows={SERIES.map((x, i) => ({ label: x.label, value: formatSignedCount(p[x.key]), color: colors[i] }))}
                          totals={p.churnRate != null ? [{ label: 'Churn rate', value: formatPct(p.churnRate) }] : []}
                        />
                      );
                    }}
                  />
                  {SERIES.map((x, i) => (
                    <Bar key={x.key} dataKey={x.key} fill={colors[i]} radius={2} isAnimationActive={false}>
                      <LabelList
                        dataKey={x.key}
                        content={(props) => {
                          const { x: bx, y, width, height, value, index } = props as {
                            x: number; y: number; width: number; height: number; value: number; index: number;
                          };
                          if (!showAllLabels && index !== points.length - 1) return null;
                          // recharts gives negative bars a negative height; the far end is y + height.
                          const below = value < 0;
                          const end = below ? Math.max(y, y + height) : Math.min(y, y + height);
                          return (
                            <text
                              x={bx + width / 2}
                              y={below ? end + 16 : end - 6}
                              textAnchor="middle"
                              className={s.valueLabel}
                            >
                              {formatSignedCount(value)}
                            </text>
                          );
                        }}
                      />
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
              <span />
            </div>

            <ChartLegend items={SERIES.map((x, i) => ({ label: x.label, color: colors[i] }))} />
          </>
        ) : (
          <div className={s.empty}>No subscriber flow data available</div>
        )}
      </div>
    </>
  );
}
